/**
 * @file Color-scheme hooks for the centralized theme.
 *
 * `useThemeMode()` — read/set the scheme ('light' | 'dark', with 'auto'
 * supported as a stored preference).
 *
 * `useThemeColors()` — resolved semantic hex values for the active scheme.
 * Use this in JS contexts where var() strings don't work: ApexCharts configs,
 * Google Maps options, canvas, color math. CSS/style contexts should keep
 * using `semanticColors` (var()-backed, zero re-renders on toggle).
 */
import { useMantineColorScheme, useComputedColorScheme } from '@mantine/core';
import { semanticPalette } from './semanticColors';
import { dataViz, dataVizDark } from './dataViz';
import { mapTokens, mapTokensDark } from './maps';

export function useThemeMode() {
  const { colorScheme, setColorScheme } = useMantineColorScheme();
  const mode = useComputedColorScheme('light', { getInitialValueInEffect: false });

  return {
    mode,
    isDark: mode === 'dark',
    preference: colorScheme,
    setMode: setColorScheme,
    toggle: () => setColorScheme(mode === 'dark' ? 'light' : 'dark')
  };
}

export function useThemeColors() {
  const { mode } = useThemeMode();
  return semanticPalette[mode];
}

export function useDataVizTokens() {
  const { isDark } = useThemeMode();
  return isDark ? dataVizDark : dataViz;
}

export function useMapTokens() {
  const { isDark } = useThemeMode();
  return isDark ? mapTokensDark : mapTokens;
}
